import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { completeSSOLogin, decodeJwtPayload } from '../lib/oauth.js';

// /auth/callback — exchange the auth code for tokens, hand to AuthContext.
// Ported from chat-portal.
export default function AuthCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const [error, setError] = useState(null);
  const ranRef = useRef(false); // guard React 18 strict-mode double-mount

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error');
    if (oauthError) { setError(params.get('error_description') || oauthError); return; }
    if (!code || !state) { setError('Missing authorization code in callback URL.'); return; }

    (async () => {
      try {
        const { accessToken, refreshToken, returnTo } = await completeSSOLogin(code, state);
        const claims = decodeJwtPayload(accessToken) || {};
        login(
          accessToken,
          { email: claims.email || '', name: claims.username || '', role: claims.role || 'viewer' },
          refreshToken,
        );
        navigate(returnTo || '/', { replace: true });
      } catch (err) {
        console.error('[AuthCallback] SSO completion failed:', err);
        setError(err.message || 'Sign-in failed.');
      }
    })();
  }, [params, login, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-4">
      <div className="w-full max-w-md border border-border rounded-lg p-8 text-center space-y-4">
        {error ? (
          <>
            <div className="font-semibold text-lg">Sign-in failed</div>
            <div className="text-sm text-destructive border border-destructive/40 bg-destructive/5 rounded-md px-3 py-2">{error}</div>
            <button onClick={() => navigate('/login?manual=1', { replace: true })}
              className="w-full px-4 py-2 rounded-md bg-primary text-primary-foreground">Back to sign-in</button>
          </>
        ) : (
          <>
            <div className="font-semibold text-lg">Signing you in…</div>
            <div className="text-sm text-muted-foreground">Completing your single sign-on.</div>
          </>
        )}
      </div>
    </div>
  );
}
